import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  ColumnDef,
  ColumnFiltersState,
  PaginationState,
  SortingState,
  VisibilityState,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { DataTable } from '@/components/data-table/data-table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useSchools } from '@/hooks/use-schools';
import type { School, SchoolStatus } from '@/types/api';
import { Plus, Search, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header';
import { SchoolLogo } from '@/components/school-logo';

const columns: ColumnDef<School>[] = [
  {
    accessorKey: 'schoolName',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="School ID" />
    ),
    cell: ({ row }) => {
      const school = row.original;
      const schoolName = row.getValue('schoolName') as string;
      return (
        <Link
          to={`/dashboard/schools/${schoolName}`}
          className="font-semibold text-primary hover:underline hover:text-primary/80 transition-colors inline-flex items-center gap-3 group-hover:translate-x-0.5 transition-transform"
        >
          <SchoolLogo school={school} size="md" />
          <span>{schoolName}</span>
        </Link>
      );
    },
  },
  {
    accessorKey: 'schoolTitle',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Name" />
    ),
    cell: ({ row }) => {
      const title = row.getValue('schoolTitle') as string;
      return <div className="font-medium text-base">{title}</div>;
    },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const status = row.getValue('status') as SchoolStatus;
      return (
        <Badge
          variant={
            status === 'live'
              ? 'default'
              : status === 'waitlist'
                ? 'secondary'
                : 'destructive'
          }
          className="font-medium px-3 py-1"
        >
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Badge>
      );
    },
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id));
    },
  },
  {
    accessorKey: 'timezone',
    header: 'Timezone',
    cell: ({ row }) => {
      const timezone = row.getValue('timezone') as string;
      // Extract timezone abbreviation (e.g., PST from America/Los_Angeles)
      const parts = timezone.split('/');
      return (
        <div className="text-sm text-muted-foreground font-medium">
          {parts[parts.length - 1].replace('_', ' ')}
        </div>
      );
    },
  },
  {
    accessorKey: 'domains',
    header: 'Domains',
    cell: ({ row }) => {
      const domains = row.getValue('domains') as string[];
      return (
        <div className="flex flex-wrap gap-1.5">
          {domains.slice(0, 2).map((domain) => (
            <Badge
              key={domain}
              variant={domain === 'joinsaturn.com' ? 'default' : 'secondary'}
              className="text-xs font-medium px-2.5 py-0.5"
            >
              {domain}
            </Badge>
          ))}
          {domains.length > 2 && (
            <Badge variant="outline" className="text-xs font-medium px-2.5 py-0.5">
              +{domains.length - 2}
            </Badge>
          )}
        </div>
      );
    },
    enableSorting: false,
  },
];

export function SchoolsPage() {
  const navigate = useNavigate();
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([
    { id: 'status', value: ['live', 'waitlist', 'suspended'] }, // Show all schools by default
  ]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  // Build API params from table state
  const statusFilter = columnFilters.find((f) => f.id === 'status');
  const sortBy = sorting[0]?.id;
  const orderBy = sorting[0]?.desc ? 'desc' : 'asc';

  // Fetch schools
  const { data, isLoading, isFetching, error } = useSchools({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: globalFilter || undefined,
    filterBy: statusFilter?.value
      ? `status:eq:${(statusFilter.value as string[]).join(',')}`
      : undefined,
    sortBy: sortBy,
    orderBy: orderBy,
    paginate: true,
    meta: true,
  });

  // Debug logging
  console.log('📊 Schools Page State:', {
    isLoading,
    isFetching,
    hasError: !!error,
    error: error?.message,
    schoolsCount: data?.schools?.length || 0,
    totalInDB: data?.total || 0,
    userCount: data?.userCount || 0,
    currentPage: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    statusFilter: statusFilter?.value,
    searchTerm: globalFilter,
    sampleSchool: data?.schools?.[0] ? {
      schoolName: data.schools[0].schoolName,
      schoolTitle: data.schools[0].schoolTitle,
      hasLogo: !!data.schools[0].logo,
      logoUrl: data.schools[0].logo?.resourceUrl,
      logoId: data.schools[0].logoId,
    } : null,
  });

  const table = useReactTable({
    data: data?.schools || [],
    columns,
    pageCount: data ? Math.ceil(data.total / pagination.pageSize) : 0,
    state: {
      pagination,
      sorting,
      columnFilters,
      globalFilter,
      columnVisibility,
    },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
  });

  return (
    <div className="container mx-auto space-y-8 p-6">
      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Error loading schools: {error.message}
          </AlertDescription>
        </Alert>
      )}

      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Schools</h1>
        <p className="text-muted-foreground mt-1">
          Search for schools or create a new school
        </p>
      </div>

      {/* Search Bar */}
      <div className="flex items-center gap-3 max-w-2xl">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search schools by name or school code..."
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className="pl-10 h-12 text-base"
          />
        </div>
        <Link 
          to="/dashboard/schools/new"
          className="h-12 px-4 flex items-center gap-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors whitespace-nowrap"
        >
          <Plus className="h-5 w-5" />
          <span className="font-medium">Create School</span>
        </Link>
      </div>

      {/* Results Table - Only show when there's a search query */}
      {globalFilter && (
        <Card className="shadow-sm">
          <CardContent className="p-0">
            {isLoading || isFetching ? (
              <div className="w-full">
                {/* Table Rows Skeleton */}
                <div className="rounded-md overflow-hidden border-0">
                  {/* Header Row */}
                  <div className="border-b bg-muted/30 h-11 px-6 pt-2 flex items-start gap-6">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-4 w-56 flex-1" />
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-4 w-40" />
                  </div>
                  
                  {/* Data Rows */}
                  {Array.from({ length: pagination.pageSize }).map((_, i) => (
                    <div key={i} className="border-b last:border-b-0 hover:bg-muted/50 transition-colors h-16">
                      <div className="flex items-center gap-6 px-6 h-full">
                        {/* School ID with Logo */}
                        <div className="flex items-center gap-3">
                          <Skeleton className="h-10 w-10 rounded-lg shrink-0" />
                          <Skeleton className="h-5 w-32" />
                        </div>
                        {/* School Name */}
                        <Skeleton className="h-5 w-56 flex-1" />
                        {/* Status Badge */}
                        <Skeleton className="h-7 w-20 rounded-full" />
                        {/* Timezone */}
                        <Skeleton className="h-4 w-28" />
                        {/* Domains */}
                        <div className="flex gap-1.5 w-40">
                          <Skeleton className="h-5 w-20 rounded-full" />
                          <Skeleton className="h-5 w-16 rounded-full" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Pagination Skeleton */}
                <div className="flex items-center justify-between px-6 py-4 border-t bg-muted/20">
                  <Skeleton className="h-4 w-48" />
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-9 w-24" />
                    <Skeleton className="h-9 w-9" />
                    <Skeleton className="h-9 w-9" />
                    <Skeleton className="h-9 w-24" />
                  </div>
                </div>
              </div>
            ) : (
              <DataTable
                table={table}
                columns={columns}
                onRowClick={(school) => navigate(`/dashboard/schools/${school.schoolName}`)}
              />
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
